import * as Sentry from '@sentry/react';
import { classes } from 'html-classes';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Address } from '~/api/DeliveryAddress';
import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';

import { checkoutStore, mainStore, orderStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';
import { Button } from '../isolated/Button';
import { ModalHeader } from '../Modal/components/ModalHeader';
import { ModalType } from '../Modal/interface';
import { deliveryAddressStore } from '../ModalDeliveryAddress/DeliveryAddressStore';

import styles from './ModalAddressList.module.scss';

const ModalAddressList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);
  const { closeModal, openModal } = useModal();

  const [selectedId, setSelectedId] = useState<string | null>(
    userStore.deliveryAddress?.addressId || null,
  );

  const handleCloseModal = () => {
    closeModal([REDIRECT_QUERY_NAME]);
  };

  const handleAddressSelect = async (address: Address) => {
    try {
      const etaCalculation = await orderStore.fetchETA(
        `${address.latitude},${address.longitude}`,
      );
      if (!etaCalculation) {
        return;
      }
      setSelectedId(address.id);
      userStore.setDeliveryAddress({
        zip: address.postcode || '',
        country: address.country_code || '',
        city: address.city || '',
        region: '',
        address1: address.street_address_1 || '',
        address2: address.street_address_2 ?? '',
        addressFloor: address.floor ?? '',
        addressBld: address.building ?? '',
        addressApt: address.apartment ?? '',
        type: address.type || (company.canDefaultAddressBeEmpty ? '' : 'home'),
        shortAddress: address.street_address_1 || '',
        coordinates: {
          lat: address.latitude,
          lng: address.longitude,
        },
        placeId: address.place_id || '',
        addressId: address.id,
        comment: address.comment || '',
        instructions: toJS(address.instructions || []),
      });
      orderStore.setEtaCalculation(etaCalculation);
      mainStore.setIsChangeAddressPopover(false);
      checkoutStore.setDeliverySlots(etaCalculation.deliverySlots || null);

      Sentry.withScope((scope) => {
        scope.setExtras({
          stage: 'address_selected',
          address: {
            addressId: address.id,
            zip: address.postcode || '',
            country: address.country_code || '',
            city: address.city || '',
            region: '',
            address1: address.street_address_1 || '',
            address2: address.street_address_2 ?? '',
            addressFloor: address.floor ?? '',
            addressBld: address.building ?? '',
            addressApt: address.apartment ?? '',
            type:
              address.type || (company.canDefaultAddressBeEmpty ? '' : 'home'),
            shortAddress: address.street_address_1 || '',
            coordinates: {
              lat: address.latitude,
              lng: address.longitude,
            },
            placeId: address.place_id || '',
            comment: address.comment || '',
            instructions: toJS(address.instructions || []),
          },
        });
        Sentry.captureMessage('[Modal Address List] Address selected', 'info');
      });

      mainStore.sendToRN('trackOneSignalOutcome', {
        name: 'warehouse_selected',
      });
      mainStore.sendToRN('setUserProperties', {
        'Address: postcode': address.postcode || '',
      });
      mainStore.sendToRN('setUserProperties', {
        'Address: lat': address.latitude,
      });
      mainStore.sendToRN('setUserProperties', {
        'Address: lon': address.longitude,
      });

      if (!redirectNext) {
        closeModal();
        return;
      }

      navigate(redirectNext, { replace: true });
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddNewAddress = () => {
    deliveryAddressStore.setEditAddressData({
      addressId: null,
      address2: '',
      addressFloor: '',
      addressBld: '',
      addressApt: '',
      type: 'home',
      comment: '',
      instructions: [],
    });
    openModal(
      ModalType.EditAddressLocation,
      ModalType.DeliveryAddressList,
      {
        [REDIRECT_QUERY_NAME]: redirectNext || '',
      },
      false,
      true,
    );
  };

  const handleEditAddress =
    (address: Address) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      deliveryAddressStore.setEditAddressData({
        addressId: address.id,
        address2: address.street_address_2 || '',
        addressFloor: address.floor || '',
        addressBld: address.building || '',
        addressApt: address.apartment || '',
        type: address.type || '',
        comment: address.comment || '',
        instructions: address.instructions || [],
      });
      openModal(ModalType.EditAddressDetails, 'delivery-address-list', {
        [MODAL_EDIT_ADDRESS_PARAM]: address.id,
        [REDIRECT_QUERY_NAME]: redirectNext || '',
      });
    };

  return (
    <Modal
      isShow={true}
      className={styles.addressList}
      onBackdropDismiss={handleCloseModal}
    >
      <div className={styles.addressList__container}>
        <ModalHeader
          size="S"
          onClose={handleCloseModal}
          title={t('checkout:deliveryAddresses')}
          className={styles.addressList__header}
        />
        <div className={styles.addressList__body}>
          {userStore.addressList.map((address) => {
            const isSelected = selectedId === address.id;

            return (
              <div
                key={address.id}
                className={classes([
                  styles.addressItem,
                  isSelected && styles.checked,
                ])}
                onClick={() => handleAddressSelect(address)}
              >
                <div className={styles.addressItemContent}>
                  <div className={styles.addressItemInfo}>
                    <div className={styles.addressItemTitle}>
                      {address.type?.toLowerCase() === 'home'
                        ? t('Home')
                        : address.type}
                    </div>
                    <div className={styles.addressItemAddress}>
                      {address.street_address_1}
                      {address.city && <>, {address.city}</>}
                    </div>
                  </div>
                  <Icon
                    type="edit"
                    size={16}
                    className={styles.addressItemEditIcon}
                    onClick={handleEditAddress(address)}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className={styles.buttonContainer}>
          <Button
            startIcon="plus"
            iconSize={24}
            size="L"
            onClick={handleAddNewAddress}
            className={styles.button}
            variant="secondary"
          >
            {t('checkout:addNewAddress')}
          </Button>
        </div>
      </div>
    </Modal>
  );
});

export default ModalAddressList;
